import { useEffect, useMemo, useRef, useState } from 'react'

import { useWatch } from 'react-hook-form'

import { usePrevious } from '@bettermode/common/hooks'
import {
  Event,
  RecurringEventFrequency,
  Space,
} from '@tribeplatform/gql-client/types'
import { useAuthMember } from '@tribeplatform/react-sdk/hooks'
import { Accordion } from '@tribeplatform/react-ui-kit/Accordion'
import { Container } from '@tribeplatform/react-ui-kit/Layout'

import { useBrowserTimeZone } from '../../common/index.js'
import { dayjs } from '../../common/lib/dayjs.js'
import { Form, FormRef } from '../../Form/index.js'
import { SimpleHtmlEditor } from '../../HtmlEditor/SimpleHtmlEditor.js'
import { T, useI18n } from '../../i18n/index.js'
import { AdminOnlyInfo } from '../../PostForm/PostForm.js'
import { PostSeoOptions } from '../../PostForm/PostSeoOptions.js'
import { Prompt } from '../../Prompt/index.js'
import { EventLocationPicker } from '../Components/EventLocationPicker.js'
import { EventFormFields } from '../types.js'

import { CoverImagePicker } from './CoverImagePicker.js'
import { EventDatePicker } from './DatePicker/EventDatePicker.js'
import { EventFormActionButtons } from './EventFormActionButtons.js'
import { EventSpacePicker } from './EventSpacePicker.js'
import { NotificationSettings } from './NotificationSettings.js'
import { useSubmitEvent } from './useSubmitEvent.js'

interface EventFormProps {
  event?: Event
  selectedSpace?: Space
  onCancel?: () => void
}

export const EventForm = ({
  event,
  selectedSpace,
  onCancel,
}: EventFormProps) => {
  const { $t } = useI18n()
  const browserTimeZone = useBrowserTimeZone()
  const { data: authMember } = useAuthMember()
  const [enablePrompt, setEnablePrompt] = useState(false)
  const formRef = useRef<FormRef<EventFormFields>>()
  const { submitEvent, isCreating, isUpdating } = useSubmitEvent({
    event,
    setEnablePrompt,
  })

  const targetSpace = useMemo(
    () => event?.space ?? selectedSpace,
    [event?.space, selectedSpace],
  )

  const defaultValues = useMemo(() => {
    const { startTime: initialStartTime, endTime: initialEndTime } =
      getInitialStartAndEndTime(event)

    const resolvedTimeZone =
      event?.timeZone ?? authMember?.timeZone ?? browserTimeZone

    const startTime = dayjs(
      dayjs(initialStartTime)
        .tz(resolvedTimeZone)
        .format('YYYY-MM-DDTHH:mm:ss'),
    ).toISOString()

    const endTime = dayjs(
      dayjs(initialEndTime).tz(resolvedTimeZone).format('YYYY-MM-DDTHH:mm:ss'),
    ).toISOString()

    return {
      title: event?.title,
      content: event?.content,
      hosts: event?.hosts ?? [authMember],
      startTime,
      endTime,
      timeZone: event?.timeZone ?? authMember?.timeZone,
      physicalLocations: event?.physicalLocations ?? [],
      virtualLocations: event?.virtualLocations ?? [],
      thumbnail: event?.thumbnail,
      capacity: event?.capacity,
      customSeoDetail: event?.customSeoDetail,
      notificationSettings: {
        notifyOnDelete: event?.notificationSettings?.notifyOnDelete ?? true,
        notifyOnRegister: event?.notificationSettings?.notifyOnRegister ?? true,
        notifyOnUpdate: event?.notificationSettings?.notifyOnUpdate ?? true,
        remindBefore: event?.notificationSettings?.remindBefore ?? true,
      },
      targetSpace,
      slug: event?.slug,
      recurrenceConfig: event?.recurrenceConfig ?? {
        frequency: RecurringEventFrequency.once,
      },
    } satisfies EventFormFields
  }, [event, authMember, targetSpace, browserTimeZone])
  const previousTargetSpace = usePrevious(targetSpace)

  useEffect(() => {
    if (previousTargetSpace?.id !== defaultValues.targetSpace?.id) {
      formRef.current?.methods.reset(defaultValues)
    }
  }, [defaultValues, previousTargetSpace])

  return (
    <Form<EventFormFields>
      ref={formRef}
      defaultValues={defaultValues}
      onChange={() => setEnablePrompt(true)}
    >
      {({ formState: { isDirty } }) => (
        <Container size="md" direction="vertical" spacing="lg">
          <Prompt
            when={enablePrompt}
            message={$t({
              id: 'Post.Alert.UnsavedChanges',
              defaultMessage:
                'You have unsaved changes. Are you sure you want to leave this page?',
            })}
          />
          {!event && <EventSpacePicker />}
          <Form.Input
            name="title"
            label={<T id="Event.Form.Title" defaultMessage="Event title" />}
            validation={{
              required: $t({
                id: 'Generic.FieldIsRequired',
                defaultMessage: 'This field is required.',
              }),
            }}
          />
          <SimpleHtmlEditor
            name="content"
            label={
              <T
                id="Event.Form.Description"
                defaultMessage="Event description"
              />
            }
            validation={{
              required: $t({
                id: 'Generic.FieldIsRequired',
                defaultMessage: 'This field is required.',
              }),
            }}
          />
          <Form.MemberPicker
            name="hosts"
            multiple
            label={
              <T id="Event.Form.OrganizedBy" defaultMessage="Organized by" />
            }
            required
          />
          <EventDatePicker event={event} />
          <EventLocationPicker />
          <CoverImagePicker />
          <EventCapacity />
          <CustomSeoOptions space={selectedSpace} />
          <NotificationSettings />
          <Form.Actions>
            <EventFormActionButtons
              isDirty={isDirty}
              onSubmit={submitEvent}
              onCancel={onCancel}
              event={event}
              loading={isCreating || isUpdating}
            />
          </Form.Actions>
        </Container>
      )}
    </Form>
  )
}

const EventCapacity = () => {
  const { $t } = useI18n()
  const capacity = useWatch({ name: 'capacity' })
  const capacityNumber = Number(capacity)
  const isCapacitySet = !Number.isNaN(capacityNumber)

  return (
    <Form.Input
      name="capacity"
      type="number"
      label={<T id="Generics.Capacity" defaultMessage="Capacity" />}
      placeholder={$t({
        id: 'Generics.Unlimited',
        defaultMessage: 'Unlimited',
      })}
      min={0}
      helperText={
        isCapacitySet && capacityNumber > 0 ? (
          <T
            id="Event.Form.Capacity.HelperText"
            defaultMessage="{capacity} {capacity, plural, one {attendee is allowed} other {attendees are allowed}}. Registration will be closed when the capacity is reached."
            values={{
              capacity: Number(capacity),
            }}
          />
        ) : null
      }
    />
  )
}

interface CustomSeoOptionsProps {
  space: Space
}

export const CustomSeoOptions = ({ space }: CustomSeoOptionsProps) => {
  return (
    <Accordion variant="chevron" iconAlign="trailing">
      <Accordion.Button>
        <div className="space-s-1">
          <span>
            <T id="Post.SeoOptions" defaultMessage="SEO options" />
          </span>
          <AdminOnlyInfo />
        </div>
      </Accordion.Button>
      <Accordion.Panel>
        <PostSeoOptions space={space} />
      </Accordion.Panel>
    </Accordion>
  )
}

const getInitialStartAndEndTime = (event?: Event) => {
  if (event) {
    return {
      startTime: event.startTime,
      endTime: event.endTime,
    }
  }

  /**
   * For the new event start end time, add 30 minutes to the current time, then round DOWN to the nearest 15-minute slot.
   */
  const currentTime = dayjs()
  const thirtyMinutesLater = currentTime.add(30, 'minute')
  const minutes = thirtyMinutesLater.minute()
  const roundedMinutes = Math.floor(minutes / 15) * 15
  const startTime = thirtyMinutesLater
    .minute(roundedMinutes)
    .second(0)
    .millisecond(0)
    .toDate()
  return {
    startTime,
    endTime: dayjs(startTime).add(1, 'hour').toDate(),
  }
}
